/** QueueList (§16.2 queues): each item shows age, owner and the next action — never a dead row. */
import { useIntl } from '@windrop/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ToneBadge, type Tone } from './StatusBadge';

export interface QueueItem {
  id: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** When the item entered the queue; age is derived from this. */
  since: string;
  /** Deadline, when one exists; overdue items get an error tone automatically. */
  deadline?: string;
  owner?: string | null;
  status?: { tone: Tone; text: string };
  nextAction: { label: string; to?: string; onClick?: () => void };
}

export function queueTone(item: QueueItem, now = Date.now()): Tone {
  if (item.deadline && Date.parse(item.deadline) < now) return 'error';
  return item.status?.tone ?? 'neutral';
}

export function QueueList({
  items,
  emptyText = 'Queue is empty.',
  now,
}: {
  items: QueueItem[];
  emptyText?: string;
  /** Test hook. */
  now?: Date;
}) {
  const intl = useIntl();
  if (items.length === 0) return <p className="wd-muted">{emptyText}</p>;
  return (
    <ul className="wdc-queue">
      {items.map((item) => {
        const tone = queueTone(item, now?.getTime());
        return (
          <li key={item.id} className="wdc-queue__item wd-surface" data-tone={tone}>
            <div className="wdc-queue__main">
              <strong>{item.title}</strong>
              {item.subtitle ? <span className="wd-muted">{item.subtitle}</span> : null}
            </div>
            <dl className="wdc-queue__facts">
              <div>
                <dt>Age</dt>
                <dd>
                  <time dateTime={item.since}>{intl.formatRelative(item.since, now)}</time>
                </dd>
              </div>
              {item.deadline ? (
                <div>
                  <dt>Deadline</dt>
                  <dd>
                    <time dateTime={item.deadline}>{intl.formatDateTime(item.deadline)}</time>
                  </dd>
                </div>
              ) : null}
              <div>
                <dt>Owner</dt>
                <dd>{item.owner ?? <span className="wd-muted">Unassigned</span>}</dd>
              </div>
            </dl>
            {item.status ? (
              <ToneBadge tone={tone}>
                {tone === 'error' && item.deadline ? 'Overdue' : item.status.text}
              </ToneBadge>
            ) : null}
            <div className="wdc-queue__action">
              {item.nextAction.to ? (
                <Link className="wd-btn" data-variant="secondary" to={item.nextAction.to}>
                  {item.nextAction.label}
                </Link>
              ) : (
                <button
                  type="button"
                  className="wd-btn"
                  data-variant="secondary"
                  onClick={item.nextAction.onClick}
                >
                  {item.nextAction.label}
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
